import { AlertStatus } from '@/entities/alert-status';
import {
  type TimeseriesLine,
  type TimeseriesMetric,
} from '@/pcs/domain/entities/pcs-timeseries';
import { AlertStatusFromValue } from '@/pcs/domain/entities/score';

export interface PCSPowerAvailabilityRow {
  'mantle_pcs_status.accure_id': string;
  'frontend_id_mapping.full_frontend_id': string;
  'mantle_pcs_status.power_availability_score': number;
  'mantle_pcs_timeseries.window_end_date': Date;
  'mantle_pcs_timeseries.power_availability_gap': number;
  'mantle_pcs_timeseries.power_availability_invalid': number;
}

export function transformPowerAvailabilityRows(
  rows: PCSPowerAvailabilityRow[],
): TimeseriesMetric {
  // 1) Create a map from ID => TimeseriesLine
  const linesMap: Record<string, TimeseriesLine> = {};

  for (const row of rows) {
    const id = row['mantle_pcs_status.accure_id'];
    const fullFrontendID = row['frontend_id_mapping.full_frontend_id'];
    if (!id) continue;

    // Ensure there's a line for this ID
    if (!linesMap[id]) {
      linesMap[id] = {
        id,
        fullFrontendID,
        idStatus:
          AlertStatusFromValue[
            row['mantle_pcs_status.power_availability_score']
          ] ?? AlertStatus.OK,
        subLines: [
          { label: 'ok', data: [] },
          { label: 'gap', data: [] },
          { label: 'invalid', data: [] },
        ],
      };
    }

    const timestamp = new Date(row['mantle_pcs_timeseries.window_end_date']);

    // Push each measure into its subLine
    linesMap[id].subLines
      .find((s) => s.label === 'ok')
      ?.data.push({
        timestamp,
        value:
          100 -
          (row['mantle_pcs_timeseries.power_availability_gap'] +
            row['mantle_pcs_timeseries.power_availability_invalid']),
      });
    linesMap[id].subLines
      .find((s) => s.label === 'gap')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_availability_gap'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'invalid')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_availability_invalid'],
      });
  }

  // 2) Derive an overall metricStatus if desired
  let metricStatus: AlertStatus = AlertStatus.OK;
  for (const line of Object.values(linesMap)) {
    if (line.idStatus === AlertStatus.CRITICAL) {
      metricStatus = AlertStatus.CRITICAL;
      break; // No need to check further
    } else if (
      line.idStatus === AlertStatus.WARNING &&
      metricStatus !== AlertStatus.WARNING
    ) {
      metricStatus = AlertStatus.WARNING;
      // keep checking in case there's CRITICAL down the line
    } else if (
      line.idStatus === AlertStatus.INFO &&
      metricStatus !== AlertStatus.INFO
    ) {
      metricStatus = AlertStatus.INFO;
      // keep checking in case there's CRITICAL down the line
    }
  }

  // 3) Return the final TimeseriesMetric
  const metric: TimeseriesMetric = {
    metricName: 'Power Availability',
    metricStatus,
    lines: Object.values(linesMap),
  };

  return metric;
}
